import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Lock } from 'lucide-react';
import { Drawer, Field, QueryState } from '@/components/live';
import { ChildRecords } from '@/components/ChildRecords';
import { GeographyPicker } from '@/screens/parts/GeographyPicker';
import { callRpc, listRows } from '@/api/resources';
import { uploadDocument } from '@/api/documents';
import { DropZone } from '@/components/DropZone';
import { useLetterhead } from '@/hooks/useLetterhead';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';

interface OrgRow {
  id: string;
  name: string;
  organization_code: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  registered_address: string | null;
  scope_of_operations: string | null;
  primary_geography_id: string | null;
  primary_geography_name: string | null;
}

interface OfficeRow {
  id: string;
  office_code: string | null;
  name: string;
  office_type: string;
  address: string | null;
  geography_name: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  is_primary: boolean;
}

const OFFICE_TYPES = [
  { id: 'head', label: 'Head office' },
  { id: 'regional', label: 'Regional office' },
  { id: 'local', label: 'Local office' },
  { id: 'field', label: 'Field office' },
];

/**
 * The organisation's own details, and the offices under it.
 *
 * These are not decoration. The joining declaration names the organisation as the member's main
 * contact for certification and tells the farmer they may ask for documents; with no contact name
 * and no address on file that sentence points nowhere. FSC-STD-30-005 Clause 8.1 obliges the group
 * entity to have given members that information, and a document can only carry what is recorded.
 *
 * Offices are a child record rather than one address field, because a group working across four
 * districts has four of them, and exactly one is primary - the database enforces that, so a
 * document needing "the" office always has one answer.
 */
export function OrganisationProfile() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const mayEdit = (profile?.tier ?? 99) <= 1 && can(profile, 'org.profile', 'update');

  const org = useQuery({
    queryKey: ['organization-profile'],
    queryFn: async () => (await listRows<OrgRow>('organizations'))[0] ?? null,
    enabled: isSupabaseConfigured && Boolean(profile),
  });

  const [form, setForm] = useState({
    contact_name: '', contact_email: '', contact_phone: '', website: '',
    registered_address: '', scope_of_operations: '', primary_geography_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  // The legal name is the platform owner's to change, as creating the organisation is; the server
  // asks for an active operator, and tier 0 is who that is.
  const mayRename = profile?.tier === 0;
  const letterhead = useLetterhead();
  const [logoBusy, setLogoBusy] = useState(false);

  // The logo is the organisation's own mark, so its admins keep it here with the address; the
  // letterhead's wording stays the app owner's (Consent templates).
  async function setLogo(file: File | null) {
    if (!profile?.organization) return;
    if (file && !['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Choose a PNG, JPEG or WebP image.');
      return;
    }
    setLogoBusy(true);
    try {
      const documentId = file
        ? (await uploadDocument({ file, bucket: 'generated-documents', organizationId: profile.organization.id, ownerId: profile.organization.id })).id
        : null;
      await callRpc('fn_save_organization_logo', { p_document_id: documentId });
      await queryClient.invalidateQueries({ queryKey: ['document_letterheads'] });
      toast.success(file ? 'Logo saved. It prints on the letterhead from now on.' : 'Logo removed.');
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'The logo could not be saved.');
    } finally {
      setLogoBusy(false);
    }
  }
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [renameError, setRenameError] = useState<string>();

  async function rename() {
    setRenameError(undefined);
    setSaving(true);
    try {
      await callRpc('fn_rename_organization', { p_name: newName });
      await queryClient.invalidateQueries({ queryKey: ['organization-profile'] });
      await queryClient.invalidateQueries({ queryKey: ['my-organizations'] });
      await queryClient.invalidateQueries({ queryKey: ['organization-overview'] });
      toast.success(`Renamed to ${newName.trim()}. The letterhead follows.`);
      setRenaming(false);
    } catch (caught) {
      setRenameError(caught instanceof Error ? caught.message : 'The name could not be changed.');
    } finally {
      setSaving(false);
    }
  }

  // The row arrives after the first render, so the form is seeded from it rather than initialised
  // with it. Re-seeding on every change would fight the person typing, hence the id dependency.
  useEffect(() => {
    const row = org.data;
    if (!row) return;
    setForm({
      contact_name: row.contact_name ?? '',
      contact_email: row.contact_email ?? '',
      contact_phone: row.contact_phone ?? '',
      website: row.website ?? '',
      registered_address: row.registered_address ?? '',
      scope_of_operations: row.scope_of_operations ?? '',
      primary_geography_id: row.primary_geography_id ?? '',
    });
  }, [org.data?.id]);

  async function save() {
    setError(undefined);
    setSaving(true);
    try {
      // Empty means "leave as it is" on the server, so clearing a field here does not blank it.
      // Said plainly rather than left for somebody to discover.
      await callRpc('fn_save_organization_profile', {
        p_contact_name: form.contact_name.trim() || null,
        p_contact_email: form.contact_email.trim() || null,
        p_contact_phone: form.contact_phone.trim() || null,
        p_website: form.website.trim() || null,
        p_registered_address: form.registered_address.trim() || null,
        p_scope_of_operations: form.scope_of_operations.trim() || null,
        p_primary_geography_id: form.primary_geography_id || null,
      });
      await queryClient.invalidateQueries({ queryKey: ['organization-profile'] });
      toast.success('Organisation details saved.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <QueryState empty="No organisation is linked to this account." query={org}>
      <section className="card">
        <div className="section-heading">
          <h3><Building2 size={15} /> {org.data?.name ?? 'Organisation'}</h3>
          <span className="muted">{org.data?.organization_code}</span>
          {mayRename && (
            <button className="button-link" onClick={() => { setNewName(org.data?.name ?? ''); setRenameError(undefined); setRenaming(true); }}
                    type="button">Rename</button>
          )}
        </div>
        <Drawer
          footer={
            <>
              <button className="cancel" onClick={() => setRenaming(false)} type="button">Cancel</button>
              <button className="primary" disabled={saving || !newName.trim()} onClick={() => void rename()} type="button">
                {saving ? 'Saving...' : 'Rename'}
              </button>
            </>
          }
          onClose={() => setRenaming(false)}
          open={renaming}
          subtitle="The registered name. It heads the letterhead on documents a farmer signs."
          title="Rename the organisation"
        >
          <Field hint="As registered, e.g. with Pvt Ltd." label="Full name" required>
            <input onChange={(event) => setNewName(event.target.value)} value={newName} />
          </Field>
          {renameError && <p className="login-error" role="alert">{renameError}</p>}
        </Drawer>

        <Field hint="Printed at the top of the letterhead on every document. PNG, JPEG or WebP." label="Logo">
          <div className="cell-stack">
            {letterhead.data?.logoDataUri
              ? <img alt={`${org.data?.name ?? 'Organisation'} logo`} className="org-logo-preview" src={letterhead.data.logoDataUri} />
              : <span className="muted">No logo yet.</span>}
            {mayEdit && (
              <>
                <DropZone accept="image/png,image/jpeg,image/webp" busy={logoBusy}
                          label={letterhead.data?.logoDataUri ? 'Drop a new logo to replace it, or' : 'Drag the logo here, or'}
                          onFile={(file) => void setLogo(file)} />
                {letterhead.data?.row?.logo_document_id && (
                  <button className="button-link" disabled={logoBusy} onClick={() => void setLogo(null)} type="button">Remove logo</button>
                )}
              </>
            )}
          </div>
        </Field>

        {!mayEdit && (
          <p className="offline-banner">
            <Lock size={13} /> You can see these details but not change them. That needs an
            organisation admin.
          </p>
        )}

        <p className="muted">
          These appear on documents a farmer signs. The joining declaration names this organisation
          as the member&rsquo;s contact for certification and tells them they may ask for the
          documents behind it &mdash; so a contact and an address have to be here for that to mean
          anything.
        </p>

        <div className="field-row">
          <Field hint="The person a member should ask for." label="Contact name">
            <input disabled={!mayEdit} onChange={(event) => setForm({ ...form, contact_name: event.target.value })}
                   value={form.contact_name} />
          </Field>
          <Field label="Contact email">
            <input disabled={!mayEdit} inputMode="email" onChange={(event) => setForm({ ...form, contact_email: event.target.value })}
                   placeholder="e.g. contact@example.org" value={form.contact_email} />
          </Field>
        </div>
        <div className="field-row">
          <Field hint="With the country code." label="Contact phone">
            <input disabled={!mayEdit} inputMode="tel" onChange={(event) => setForm({ ...form, contact_phone: event.target.value })}
                   placeholder="e.g. +918008512341" value={form.contact_phone} />
          </Field>
          <Field label="Website">
            <input disabled={!mayEdit} onChange={(event) => setForm({ ...form, website: event.target.value })}
                   placeholder="e.g. https://example.org" value={form.website} />
          </Field>
        </div>

        <Field label="Registered address">
          <textarea disabled={!mayEdit} onChange={(event) => setForm({ ...form, registered_address: event.target.value })}
                    rows={3} value={form.registered_address} />
        </Field>

        <Field hint="What this group does and where. Printed on member-facing documents."
               label="Scope of operations">
          <textarea disabled={!mayEdit} onChange={(event) => setForm({ ...form, scope_of_operations: event.target.value })}
                    rows={3} value={form.scope_of_operations} />
        </Field>

        {/* Where the organisation is based. Not a permission: who sees which records is
            geography SCOPE, on the user, and nothing here changes it. */}
        <GeographyPicker label="Based in" required={false}
                         onChange={(id) => setForm({ ...form, primary_geography_id: id })}
                         value={form.primary_geography_id} />

        {error && <p className="field-error" role="alert">{error}</p>}

        {mayEdit && (
          <div className="inline-form-actions">
            <button className="primary" disabled={saving} onClick={() => void save()} type="button">
              {saving ? 'Saving...' : 'Save details'}
            </button>
          </div>
        )}
      </section>

      <ChildRecords<OfficeRow>
        addLabel="Add office"
        emptyText="No offices recorded yet."
        fields={[
          { key: 'name', label: 'Office name', type: 'text', required: true, placeholder: 'e.g. Chikkamagaluru field office' },
          { key: 'office_type', label: 'Type', type: 'select', options: OFFICE_TYPES, required: true },
          { key: 'address', label: 'Address', type: 'textarea', wide: true,
            hint: 'Printed on emails and documents sent to members this office serves.' },
          // The STATE, not a village. An outbound email picks the office serving the reader's
          // state and falls back to the head office, so a village recorded here would be narrowed
          // to its state anyway - and asking for one would imply a precision that is not used.
          { key: 'geography_id', label: 'State it serves', type: 'village', geographyType: 'state',
            hint: 'Members in this state are given this office\u2019s address. Leave empty for an '
              + 'office that serves no particular state.' },
          { key: 'contact_name', label: 'Contact name', type: 'text' },
          // inputMode 'tel' and 'email' make the form engine validate AND normalise these, so a
          // bare ten digits becomes +91... rather than being refused by the column.
          { key: 'contact_phone', label: 'Contact phone', type: 'text', inputMode: 'tel', placeholder: 'e.g. 9800000000' },
          { key: 'contact_email', label: 'Contact email', type: 'text', inputMode: 'email' },
          { key: 'is_primary', label: 'This is the main office', type: 'check',
            hint: 'Only one office can be the main one. Documents that name a single office use it.' },
          { key: 'remarks', label: 'Remarks', type: 'textarea', wide: true },
        ]}
        defaults={{ office_type: 'local' }}
        // No parent columns and no filter: an office belongs to the organisation, which RLS
        // already scopes and whose id the column defaults to. ownerId is only what an uploaded
        // file would hang off.
        filters={{}}
        ownerId={org.data?.id ?? ''}
        parent={{}}
        screenKey="org.profile"
        summary={(row) => ({
          title: `${row.name}${row.is_primary ? ' (main)' : ''}`,
          detail: [OFFICE_TYPES.find((t) => t.id === row.office_type)?.label, row.geography_name,
            row.address, row.contact_name, row.contact_phone, row.office_code]
            .filter(Boolean).join(' · '),
        })}
        table="org.offices"
        title="Offices"
        view="offices" />
    </QueryState>
  );
}
